import {
  SignoutResponse,
  User,
  UserManager,
  UserManagerSettings,
} from 'oidc-client-ts';
import { useAuthStore } from 'stores/auth';
import { useRouter } from 'vue-router';
import { useEnvsStore } from 'stores/envs';

export default class AuthService {
  private userManager: UserManager;

  constructor(appRoot: string) {
    const $router = useRouter();
    const $envsStore = useEnvsStore();

    const settings: UserManagerSettings = {
      authority: $envsStore.OIDC_AUTH_DOMAIN as string,
      client_id: $envsStore.OIDC_CLIENT_ID as string,
      redirect_uri: `${appRoot}/callback`,
      response_type: 'code',
      scope: $envsStore.OIDC_CLIENT_SCOPE,
      post_logout_redirect_uri: `${appRoot}/logout-success`,
      filterProtocolClaims: true,
      loadUserInfo: true,
      popupWindowFeatures: { width: 1000, height: 1000 },

      automaticSilentRenew: false,
      silent_redirect_uri: `${appRoot}/silent-renew`,
      includeIdTokenInSilentRenew: true,
      accessTokenExpiringNotificationTimeInSeconds: 10,
    };

    this.userManager = new UserManager(settings);

    this.userManager.events.addUserLoaded((user) => {
      this.setOidcUserInLocalStorage(user);
      this.getOidcUserFromLocalStorage();
    });

    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('AccessToken is about to expire, attempting silent renewal...');

      this.signinSilent()
        .then((user: User | null) => {
          if (user) {
            console.log('Silent renewal succeeded.');
          } else {
            console.log('Silent renewal completed, but no user information was returned.');
          }
        })
        .catch(error => {
          console.error('Silent renewal failed with error:', error);
        });
    });

    this.userManager.events.addAccessTokenExpired(() => {
      console.log('AccessToken Expired');

      sessionStorage.removeItem(this.userStoreKey());
      localStorage.removeItem(this.userStoreKey());

      void $router.go(0);
    });
  }

  public getUser(): Promise<User | null> {
    return this.userManager.getUser();
  }

  public signinPopup(): Promise<User> {
    return this.userManager.signinPopup();
  }

  public signinPopupCallback(): Promise<void> {
    return this.userManager.signinPopupCallback();
  }

  public signoutRedirect(): Promise<void> {
    return this.userManager.signoutRedirect();
  }

  public signoutRedirectCallback(): Promise<SignoutResponse> {
    return this.userManager.signoutRedirectCallback();
  }

  public signinSilentCallback(): Promise<void> {
    return this.userManager.signinSilentCallback();
  }

  public signinSilent(): Promise<User | null> {
    return this.userManager.signinSilent();
  }

  public userStoreKey() {
    const $envsStore = useEnvsStore();
    return `oidc.user:${$envsStore.OIDC_AUTH_DOMAIN}:${$envsStore.OIDC_CLIENT_ID}`;
  }

  public setOidcUserInLocalStorage(user: User) {
    return localStorage.setItem(this.userStoreKey(), JSON.stringify(user));
  }

  public getOidcUserFromLocalStorage(): User | null {
    const user = localStorage.getItem(this.userStoreKey());
    if (user) {
      const $store = useAuthStore();
      $store.$patch((state) => {
        state.oidc_user = JSON.parse(user);
      });

      return JSON.parse(user);
    }
    return null;
  }

  public getOidcUserFromSessionStorage(): User | null {
    const user = sessionStorage.getItem(this.userStoreKey());
    if (user) {
      const $store = useAuthStore();
      $store.$patch((state) => {
        state.oidc_user = JSON.parse(user);
      });

      return JSON.parse(user);
    }
    return null;
  }

  public storeUser(user: User) {
    return sessionStorage.setItem(this.userStoreKey(), JSON.stringify(user));
  }
}
